/**
 * Cities the RunCards network actually serves — snapshot from the `cities`
 * MongoDB collection on 2026-06-02. Drives the animated map in ForRunClubsSection.
 *
 * The DB stores city names only (no lat/lng), so we group each real city under
 * its metro and provide x,y coordinates inside a stylized 400×280 viewBox
 * (NOT real lat/lng — just an abstract layout that reads as a world map).
 *
 * When new cities are added to the DB, append them under the right metro below.
 * If a brand-new metro appears, add it to METRO_COORDS with a viewBox position.
 *
 * Source list (41 cities, 2026-06-02):
 *   Atlanta, Austin, Boca Raton, Boston, Brooklyn, Cambridge, Chamblee,
 *   Coconut Grove, Coral Gables, Culver City, Decatur, Denver, Doral,
 *   Edgewater, Encinitas, Englewood, Fort Lauderdale, Kendall West,
 *   Long Beach, Los Angeles, Miami, Miami Beach, Mill Valley, New York,
 *   North Bay Village, Oceanside, Paris, Pembroke Pines, Philadelphia,
 *   Portland, San Diego, San Francisco, Santa Monica, Seattle, Signal Hill,
 *   Somerville, Sunny Isles Beach, Sunrise, Toulouse, Venice, Weston.
 */

// Per-metro: abstract viewBox coords (legacy 2D map) + real lat/lng (3D globe).
// lat/lng are well-known city centroids.
export const METRO_COORDS = {
  NYC:         { x: 168, y: 105, lat: 40.7128,  lng: -74.0060,  region: "northeast-us" },
  BOSTON:      { x: 180, y: 92,  lat: 42.3601,  lng: -71.0589,  region: "northeast-us" },
  PHILLY:      { x: 170, y: 115, lat: 39.9526,  lng: -75.1652,  region: "northeast-us" },
  MIAMI:       { x: 165, y: 200, lat: 25.7617,  lng: -80.1918,  region: "southeast-us" },
  ATLANTA:     { x: 145, y: 165, lat: 33.7490,  lng: -84.3880,  region: "southeast-us" },
  AUSTIN:      { x: 110, y: 175, lat: 30.2672,  lng: -97.7431,  region: "south-us" },
  DENVER:      { x: 95,  y: 135, lat: 39.7392,  lng: -104.9903, region: "mountain-us" },
  SEATTLE:     { x: 40,  y: 70,  lat: 47.6062,  lng: -122.3321, region: "pnw-us" },
  PORTLAND:    { x: 38,  y: 90,  lat: 45.5152,  lng: -122.6784, region: "pnw-us" },
  "SF BAY":    { x: 35,  y: 125, lat: 37.7749,  lng: -122.4194, region: "west-us" },
  LA:          { x: 50,  y: 155, lat: 34.0522,  lng: -118.2437, region: "west-us" },
  "SAN DIEGO": { x: 55,  y: 168, lat: 32.7157,  lng: -117.1611, region: "west-us" },
  PARIS:       { x: 245, y: 110, lat: 48.8566,  lng: 2.3522,    region: "europe" },
  TOULOUSE:    { x: 242, y: 130, lat: 43.6047,  lng: 1.4442,    region: "europe" },
};

// Map every real city in the DB to its metro.
// Cities not present in METRO_COORDS get silently dropped from the animation.
export const CITY_TO_METRO = {
  "New York":          "NYC",
  Brooklyn:            "NYC",
  Boston:              "BOSTON",
  Cambridge:           "BOSTON",
  Somerville:          "BOSTON",
  Philadelphia:        "PHILLY",
  // South Florida — all roll up to MIAMI
  Miami:               "MIAMI",
  "Miami Beach":       "MIAMI",
  "Coconut Grove":     "MIAMI",
  "Coral Gables":      "MIAMI",
  Doral:               "MIAMI",
  "Boca Raton":        "MIAMI",
  "Fort Lauderdale":   "MIAMI",
  "Kendall West":      "MIAMI",
  "North Bay Village": "MIAMI",
  "Pembroke Pines":    "MIAMI",
  "Sunny Isles Beach": "MIAMI",
  Sunrise:             "MIAMI",
  Weston:              "MIAMI",
  // Atlanta metro
  Atlanta:             "ATLANTA",
  Decatur:             "ATLANTA",
  Chamblee:            "ATLANTA",
  // Texas
  Austin:              "AUSTIN",
  // Colorado — Denver metro
  Denver:              "DENVER",
  Edgewater:           "DENVER",
  Englewood:           "DENVER",
  // Pacific Northwest
  Seattle:             "SEATTLE",
  Portland:            "PORTLAND",
  // California — Bay Area
  "San Francisco":     "SF BAY",
  "Mill Valley":       "SF BAY",
  // California — LA metro (Venice = Venice Beach)
  "Los Angeles":       "LA",
  "Culver City":       "LA",
  "Long Beach":        "LA",
  "Santa Monica":      "LA",
  "Signal Hill":       "LA",
  Venice:              "LA",
  // California — San Diego metro
  "San Diego":         "SAN DIEGO",
  Encinitas:           "SAN DIEGO",
  Oceanside:           "SAN DIEGO",
  // Europe
  Paris:               "PARIS",
  Toulouse:            "TOULOUSE",
};

// Derived: per-metro city count + coords.
// Aggregated once at module load — cheap, no re-compute on every render.
function buildNetworkMetros() {
  const counts = {};
  for (const metro of Object.values(CITY_TO_METRO)) {
    counts[metro] = (counts[metro] || 0) + 1;
  }
  return Object.entries(METRO_COORDS).map(([name, coords]) => ({
    name,
    count: counts[name] || 0,
    ...coords,
  }));
}

export const NETWORK_METROS = buildNetworkMetros();

/** Top N metros by city count — these get the pulse rings + labels. */
export function topMetrosByCount(n = 5) {
  return [...NETWORK_METROS].sort((a, b) => b.count - a.count).slice(0, n);
}

/** Total cities currently in the network — used for live counter pill. */
export const TOTAL_NETWORK_CITIES = Object.keys(CITY_TO_METRO).length;
